import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useDisclosure,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    Alert,
    AlertIcon,
    Icon,
    IconButton,
    Show
} from '@chakra-ui/react';

import { LandClass, LandInterface } from '../../../core/land';

import LandForm from './landForm';
import { EditIcon } from '@chakra-ui/icons';

export default function LandModal(props: {
    as: 'button' | 'variant' | 'icon' | 'text';
    beforeClose?: Function;
    afterClose?: Function;
    data?: LandClass;
    initiallyOpen?: boolean;
}): React.ReactElement {
    const { t } = useTranslation();
    const { isOpen, onOpen, onClose } = useDisclosure({
        defaultIsOpen: props.initiallyOpen || false
    });

    const [saving, setSaving] = useState<boolean>(false);
    const isEdit = props.data !== undefined;

    const onSubmit = (
        e: React.FormEvent<HTMLFormElement>,
        land: LandInterface
    ) => {
        props.beforeClose && props.beforeClose(e, land);
        onClose();
        props.afterClose && props.afterClose(e, land);
    };

    return (
        <>
            {props.as === 'button' && (
                <Button
                    w='full'
                    size='md'
                    bg={'green.600'}
                    colorScheme='green'
                    onClick={onOpen}
                >
                    {t(isEdit ? 'form.edit' : 'land.add')}
                </Button>
            )}

            {props.as === 'variant' && (
                <Button
                    size='sm'
                    colorScheme='green'
                    variant='outline'
                    onClick={onOpen}
                >
                    {t(isEdit ? 'form.edit' : 'land.add')}
                </Button>
            )}

            {props.as === 'icon' && (
                <Show below='md'>
                    <IconButton
                        size={'sm'}
                        variant='outline'
                        aria-label='Edit land'
                        icon={<Icon as={EditIcon} />}
                        onClick={onOpen}
                    />
                </Show>
            )}

            {props.as === 'text' && (
                <Text
                    w='full'
                    fontSize='md'
                    style={{ fontWeight: 'bold' }}
                    color='gray.500'
                    onClick={onOpen}
                >
                    {t('land.add')}
                </Text>
            )}

            <Modal
                isOpen={isOpen}
                onClose={onClose}
                isCentered
                closeOnEsc={!props.initiallyOpen}
                closeOnOverlayClick={!props.initiallyOpen}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {isEdit ? t('modal.editLand') : t('modal.createLand')}
                    </ModalHeader>
                    {!props.initiallyOpen && <ModalCloseButton />}
                    <ModalBody>
                        {props.initiallyOpen && (
                            <Alert status='info' mb='4'>
                                <AlertIcon />
                                {t('land.first')}
                            </Alert>
                        )}
                        <LandForm
                            data={props.data}
                            setState={setSaving}
                            onSubmit={onSubmit}
                        />
                    </ModalBody>

                    <ModalFooter>
                        {!props.initiallyOpen && (
                            <Button
                                colorScheme='orange'
                                variant='outline'
                                size='sm'
                                mr={3}
                                onClick={onClose}
                            >
                                {t('form.close')}
                            </Button>
                        )}
                        <Button
                            colorScheme='green'
                            variant='outline'
                            size='sm'
                            type='submit'
                            form='landForm'
                            isLoading={saving}
                        >
                            {t('form.save')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
